import { AmbientLight, DirectionalLight, Group, HemisphereLight, Scene } from 'three';
import {
  bakeStaticLight,
  harvestPointLights,
  type BakeReport,
  type StaticLight,
} from '../render/bake-light';
import { LightDirector } from '../render/light-director';
import { ensureVertexColors } from '../render/materials';
import { ATMOS, LIGHTS, SHADOWS } from '../render/palette';
import { MALL } from '../world/mall';
import { buildMall, type BuiltMall } from '../world/plan';

/**
 * The real map, lit and staged.
 *
 * `mall.ts` is the floorplan and `plan.ts` turns it into geometry; this file is
 * everything that is true of the building as a whole — the moon, the ambient
 * bounce, and the fixed cameras the screenshot harness reviews it from.
 */

export const MALL_CAMERAS = {
  /** Arriving. Reference panel 5, from outside the south doors. */
  approach: { pos: [0, 1.72, 34] as const, look: [0, 4.0, 6] as const },
  /** Standing in the atrium looking the full length of the building. */
  atrium: { pos: [0, 1.72, 18] as const, look: [0, 3.4, -30] as const },
  /** Up through the canopy — the shot the moon has to land in. Aimed at the
   *  moon's own bearing, so if `LIGHTS.moon.position` moves this shot follows. */
  canopy: { pos: [0, 1.72, 16.5] as const, look: [0.5, 11, -20] as const },
  /** Level one, over the void. Panel 6. */
  gallery: { pos: [-10, 6.9, 12] as const, look: [-12.5, 6.4, -20] as const },
  /** The bridge, looking back south down the escalators. */
  bridge: { pos: [0, 6.9, 0] as const, look: [0, 3.0, 20] as const },
  /** The mascot store, from the concourse. */
  jewellery: { pos: [-6.0, 1.7, -22] as const, look: [-13.5, 2.2, -25] as const },
  /**
   * Store interiors, one camera each.
   *
   * The shop runs are laid out by accumulating unit widths from the run's `from`
   * edge, so these z values are not arbitrary — they are the computed centres of
   * their units. If a unit's width changes in `mall.ts`, every camera downstream
   * of it on the same run slides and has to be re-derived. That is the cost of
   * data-driven placement, and it is cheaper than hand-authoring the floorplan.
   */
  /**
   * Just inside the shutter, not two metres into the room.
   *
   * This unit is shuttered — the concourse side shows nothing, so the review has
   * to stand inside it, which is also where a thief who has cut the shutter is
   * standing. The old position was 1.7m off a loot counter and two thirds of the
   * frame was that one unlit box; from the threshold the whole horseshoe of cases
   * is in shot, which is the thing this store is built around.
   */
  vitrine: { pos: [-13.4, 1.62, -16.2] as const, look: [-20.6, 1.35, -16.2] as const },
  electronics: { pos: [-15.0, 1.7, -25.5] as const, look: [-20.5, 1.4, -27.4] as const },
  medieval: { pos: [-14.8, 6.85, -23.4] as const, look: [-20.5, 6.4, -23.4] as const },
  clothing: { pos: [14.8, 1.65, -14.8] as const, look: [20.5, 1.3, -15.6] as const },
  supermarket: { pos: [14.8, 1.7, -35.5] as const, look: [20.5, 1.4, -36.2] as const },
  /** Backrooms. Reference panel 4. */
  service: { pos: [-22.75, 1.7, 14] as const, look: [-22.75, 1.9, -24] as const },
  /** The guard's room. Reference panel 9. */
  control: { pos: [-25.6, 1.7, -3] as const, look: [-32.4, 1.5, -3] as const },
  /** Dead food court. Reference panel 8. */
  foodcourt: { pos: [0, 1.72, -44] as const, look: [-4, 3.0, -62] as const },
  /** Where the money goes. */
  dock: { pos: [26.5, 1.72, -25] as const, look: [33, 1.6, -33] as const },
  /** Establishing shot from above the void. */
  wide: { pos: [0, 9.5, 20] as const, look: [0, 3.0, -34] as const },
} as const;

export const MALL_ATMOSPHERE = {
  color: ATMOS.fogInterior,
  density: ATMOS.density.atrium,
};

/**
 * How many lights the game may have that actually move.
 *
 * Everything static is baked, so this is spent entirely on torches, strobes and
 * whatever else has to follow a player around. Four rather than eight because
 * the fixed cost is now zero and there is no reason to carry a budget that is
 * not being used — every one of these is evaluated by every lit fragment on
 * screen. See the measurements in `bakeStaticLight`.
 */
const DYNAMIC_LIGHT_BUDGET = 4;

let built: BuiltMall | null = null;
let director: LightDirector | null = null;
let harvested: StaticLight[] = [];
let bake: BakeReport | null = null;
let baking: Promise<BakeReport> | null = null;
let encoded: ArrayBuffer | null = null;

/** Where `tools/bake.ts` writes, and where the page looks. */
const LIGHTMAP_URL = 'lightmap.bin';

/** `?rebake` forces the slow path, which is how a new lightmap gets made. */
function wantsEncoded(): boolean {
  return typeof location !== 'undefined' && new URLSearchParams(location.search).has('rebake');
}

/**
 * The lightmap, if there is one.
 *
 * A miss is completely normal — a fresh clone has no `public/lightmap.bin`, and
 * `?rebake` deliberately ignores it — so this never rejects and never blocks:
 * the worst case is the forty-seven second bake the file exists to avoid, and
 * the mall is live and moonlit throughout either way.
 */
async function fetchLightmap(): Promise<ArrayBuffer | null> {
  if (typeof fetch !== 'function' || wantsEncoded()) return null;
  const base = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
  try {
    const res = await fetch(`${base}${LIGHTMAP_URL}`, { cache: 'force-cache' });
    if (!res.ok) return null;
    return await res.arrayBuffer();
  } catch {
    return null;
  }
}

/** The encodable lightmap from a `?rebake` load. Null on every other path. */
export function mallBakeEncoded(): ArrayBuffer | null {
  return encoded;
}

export function buildMallScene(scene: Scene): BuiltMall {
  const root = new Group();
  root.name = 'mall-scene';

  built = buildMall(MALL);
  root.add(built.root);

  // --- The light rig -----------------------------------------------------
  // One shadow-casting directional light for the moon, one hemisphere for the
  // bounce, one flat fill. Everything else in the building — every vitrine,
  // cooler and fluorescent batten — is authored as a real light and then handed
  // to the light director, which renders at most eight of them at a time. See
  // `LightDirector`: forty-seven simultaneous point lights ran at three frames
  // a second.
  const moon = new DirectionalLight(LIGHTS.moon.color, LIGHTS.moon.intensity);
  moon.position.set(...(LIGHTS.moon.position as unknown as [number, number, number]));
  moon.castShadow = true;
  moon.shadow.mapSize.set(SHADOWS.mapSize, SHADOWS.mapSize);
  moon.shadow.bias = SHADOWS.bias;
  moon.shadow.normalBias = SHADOWS.normalBias;
  const e = SHADOWS.cameraExtent;
  moon.shadow.camera.left = -e;
  moon.shadow.camera.right = e;
  moon.shadow.camera.top = e;
  moon.shadow.camera.bottom = -e;
  moon.shadow.camera.near = 1;
  moon.shadow.camera.far = 260;
  // Aim it at the middle of the building rather than the origin, or half the map
  // sits outside the shadow frustum.
  moon.target.position.set(0, 0, -14);
  root.add(moon.target);
  root.add(moon);

  root.add(
    new HemisphereLight(LIGHTS.ambient.sky, LIGHTS.ambient.ground, LIGHTS.ambient.intensity),
  );
  // Flat fill so deep service corridors never go fully black. Neutral slate,
  // NOT the hemisphere's sky colour: hemisphere light ignores occlusion, so
  // leaning on it to carry interiors paints sealed back-of-house corridors the
  // colour of the night sky. The floor lift has to come from something that has
  // no direction to give away.
  root.add(new AmbientLight(LIGHTS.ambient.fill, 0.34));

  // Last thing before the scene goes live: backfill vertex colours on anything
  // built from a raw geometry, or it renders as a black cutout. See the note on
  // `ensureVertexColors` — this is a trap, not a nicety.
  ensureVertexColors(root);

  // Then take the shop lights away from the shops and paint what they did into
  // the geometry. Must run after the whole building exists and after static
  // batching, because it reads world positions and rewrites geometry in place.
  //
  // Harvesting is synchronous and the baking is not, so between this line and
  // the promise resolving the mall renders by moonlight alone. That is on
  // purpose: the alternative is a black tab for several seconds, and a building
  // that fills in as its lights come up is a better thing to look at than
  // nothing. `mallBakeReady` is how the caller knows it has finished.
  const lights = harvestPointLights(root);
  // Kept, not just consumed. Once the fittings are inside the geometry there is
  // nothing left in the scene graph that knows where the mall is bright, and
  // the guards need to know exactly that — see `ExposureField`.
  harvested = lights;
  baking = fetchLightmap()
    .then((cache) =>
      bakeStaticLight(root, lights, {
        cache,
        // Only `tools/bake.ts` asks for this, and only it should: encoding costs
        // a copy of every baked vertex in the building.
        onEncoded: wantsEncoded() ? (buf) => (encoded = buf) : undefined,
      }),
    )
    .then((report) => {
      bake = report;
      return report;
    });

  // The pool that remains is for lights that MOVE — a torch, an alarm strobe, a
  // player. It starts empty. Its size is the hard budget for the whole game;
  // the table in `bakeStaticLight` is what happens if it is exceeded.
  director = new LightDirector(root, DYNAMIC_LIGHT_BUDGET);

  scene.add(root);
  return built;
}

export function mallData(): BuiltMall {
  if (!built) throw new Error('mall scene not built yet');
  return built;
}

/** The pool for moving lights. Needs `update(camera, dt)` once per frame. */
export function mallLights(): LightDirector {
  if (!director) throw new Error('mall scene not built yet');
  return director;
}

/**
 * The shop fittings, as data, after they were taken out of the scene.
 *
 * This is the building's own record of where it is bright. It is read by the
 * concealment sampler and by nothing else; if it is ever needed for rendering
 * again, the light director is the thing to hand it to, not the scene.
 */
export function mallStaticLights(): readonly StaticLight[] {
  return harvested;
}

/** What the lightmapper did, for the QA harness. Null until the bake lands. */
export function mallBake(): BakeReport | null {
  return bake;
}

/** Resolves when the shop lights are in the geometry. */
export function mallBakeReady(): Promise<BakeReport> {
  if (!baking) throw new Error('mall scene not built yet');
  return baking;
}
